// Accordion group definitions for the AI feature-groups list (LookAIPopupFeatureGroups /
// UiPopupAIContentFeatureGroups). Each definition buckets a set of featureGroup partIds under
// one collapsible "title" section, similar in spirit to the CONTEXT_MENU_* / LOOK_WALLS arrays
// in "../look/core/constants" - grouped here (rather than reusing those directly everywhere)
// because the AI panel's grouping is its own concept and is expected to keep changing
// independently of the context-menu groupings.
//
// A groupObject whose partId isn't listed in any definition below falls through to a trailing
// "Other" section (see buildAiAccordionSections) instead of being dropped.
import {
  COUNTERTOP_GROUP_BATHROOM,
  CABINET_GROUP_BATHROOM,
  CONTEXT_MENU_BATHTUB_BUTTONS,
  CONTEXT_MENU_SPLIT_WALL,
  CONTEXT_MENU_SHOWER_ALL,
  CONTEXT_MENU_SHOWER_OTHER,
  CONTEXT_MENU_SHOWER_ACCESSORIES,
  LOOK_WALLS_ONLY,
} from "../look/core/constants";
import {COUNTERTOP_GROUP, CABINET_GROUP, EDGE_GROUP} from "../kvComponent/core/constants";

export interface AiAccordionGroupDefinition {
  title: string;
  groups: string[]; // featureGroup partIds that belong under this title
}

// groupObject shape is intentionally loose here (partId, featureGroup, enabled, ...) - it's
// whatever LookAIPopupContent/aiFunctions.getAIInitialGroups builds, not owned by this file.
export interface AiAccordionGroupObject {
  partId?: string;
  featureGroup?: {partId?: string};
  enabled?: boolean;
  [key: string]: any;
}

// The runtime shape a rendered accordion needs: a title, the groupObjects that landed in it,
// and its expanded/collapsed state. `opened` defaults to true and is otherwise owned by the
// component (it changes when the user minimizes/maximizes a section).
export interface AiAccordionSection {
  title: string;
  groups: AiAccordionGroupObject[];
  opened: boolean;
}

export const OTHER_ACCORDION_TITLE = "Other";

// Rollout flag for the accordion grouping in LookAIPopupFeatureGroups: false keeps the
// original flat, ungrouped list (no accordions, no parent toggles) regardless of how many
// sections buildAiAccordionSections would produce. Flip to true once the grouping is ready
// to ship.
export const useAiGroupsAccordions = false;

// ---------------------------------------------------------------------------------------------
// Kitchen
// ---------------------------------------------------------------------------------------------

// "Island Cabinets" (center_island_cabinet) is bundled in with the countertop groups on
// purpose - from the AI panel's point of view the island is one counter area, and its cabinet
// row is presented next to Countertop / Backsplash rather than under "All Cabinets".
export const KITCHEN_ALL_COUNTERTOPS_GROUP = [...COUNTERTOP_GROUP, ...EDGE_GROUP, "center_island_cabinet"];
export const KITCHEN_ALL_CABINETS_GROUP = CABINET_GROUP.filter(partId => partId !== "center_island_cabinet");
export const KITCHEN_ALL_PLUMBING_GROUP = ["sink", "faucet", "center_island_sink", "center_island_faucet"];
export const KITCHEN_SURFACES_GROUP = ["kitchen_floor", "left_wall", "right_wall", "popout_wall", "walls_ceiling"];
export const KITCHEN_ALL_APPLIANCES_GROUP = ["fridge", "range", "hood", "microwave", "dishwasher",
  "center_island_range", "center_island_stovetop"];

export const KITCHEN_AI_ACCORDION_GROUPS: AiAccordionGroupDefinition[] = [
  {title: "All Countertops", groups: KITCHEN_ALL_COUNTERTOPS_GROUP},
  {title: "All Cabinets", groups: KITCHEN_ALL_CABINETS_GROUP},
  {title: "All Plumbing", groups: KITCHEN_ALL_PLUMBING_GROUP},
  {title: "Surfaces", groups: KITCHEN_SURFACES_GROUP},
  {title: "All Appliances", groups: KITCHEN_ALL_APPLIANCES_GROUP},
];

// ---------------------------------------------------------------------------------------------
// Bathroom
// ---------------------------------------------------------------------------------------------

export const BATHROOM_ALL_COUNTERTOPS_GROUP = [...COUNTERTOP_GROUP_BATHROOM];
export const BATHROOM_ALL_CABINETS_GROUP = [...CABINET_GROUP_BATHROOM];
export const BATHROOM_ALL_PLUMBING_GROUP = ["vanity_faucet", "vanity_sink"];
export const BATHROOM_ALL_BATH_SHOWER_GROUP = [
  ...CONTEXT_MENU_BATHTUB_BUTTONS,
  ...CONTEXT_MENU_SPLIT_WALL,
  ...CONTEXT_MENU_SHOWER_ALL,
  ...CONTEXT_MENU_SHOWER_OTHER,
  ...CONTEXT_MENU_SHOWER_ACCESSORIES,
];
export const BATHROOM_SURFACES_GROUP = [...LOOK_WALLS_ONLY, "bathroom_floor"];

export const BATHROOM_AI_ACCORDION_GROUPS: AiAccordionGroupDefinition[] = [
  {title: "All Countertops", groups: BATHROOM_ALL_COUNTERTOPS_GROUP},
  {title: "All Cabinets", groups: BATHROOM_ALL_CABINETS_GROUP},
  {title: "All Plumbing", groups: BATHROOM_ALL_PLUMBING_GROUP},
  {title: "Bath & Shower", groups: BATHROOM_ALL_BATH_SHOWER_GROUP},
  {title: "Surfaces", groups: BATHROOM_SURFACES_GROUP},
];

export const AI_ACCORDION_GROUPS_BY_VISUALIZER_TYPE: Record<string, AiAccordionGroupDefinition[]> = {
  kitchen: KITCHEN_AI_ACCORDION_GROUPS,
  bathroom: BATHROOM_AI_ACCORDION_GROUPS,
};

export const getAiAccordionGroupDefinitions = (visualizerType?: string): AiAccordionGroupDefinition[] => {
  return (visualizerType && AI_ACCORDION_GROUPS_BY_VISUALIZER_TYPE[visualizerType]) || [];
};

const getGroupObjectPartId = (groupObject: AiAccordionGroupObject): string | undefined => {
  return groupObject.partId || groupObject.featureGroup?.partId;
};

/**
 * Buckets a flat list of AI groupObjects into accordion sections for the given visualizer type.
 * - Sections with no matching groupObjects are dropped (nothing to show/minimize).
 * - Any groupObject that isn't covered by a definition lands in a trailing "Other" section.
 * - `opened` defaults to true; the caller (component) owns flipping it afterwards.
 */
export const buildAiAccordionSections = (
  groupObjects: AiAccordionGroupObject[],
  visualizerType?: string
): AiAccordionSection[] => {
  const definitions = getAiAccordionGroupDefinitions(visualizerType);
  const usedPartIds = new Set<string>();

  const sections: AiAccordionSection[] = definitions
    .map(definition => {
      const groups = groupObjects.filter(groupObject => {
        const partId = getGroupObjectPartId(groupObject);
        return !!partId && definition.groups.includes(partId);
      });
      groups.forEach(groupObject => {
        const partId = getGroupObjectPartId(groupObject);
        if (partId) {
          usedPartIds.add(partId);
        }
      });
      return {title: definition.title, groups, opened: true};
    })
    .filter(section => section.groups.length > 0);

  const otherGroups = groupObjects.filter(groupObject => {
    const partId = getGroupObjectPartId(groupObject);
    return !!partId && !usedPartIds.has(partId);
  });
  if (otherGroups.length > 0) {
    sections.push({title: OTHER_ACCORDION_TITLE, groups: otherGroups, opened: true});
  }

  return sections;
};

/**
 * Summarized on/off state for a section's parent toggle: checked only when every group inside
 * is enabled, so a single disabled group inside unchecks the whole section's toggle.
 */
export const isAccordionSectionEnabled = (section: AiAccordionSection): boolean => {
  return section.groups.length > 0 && section.groups.every(groupObject => !!groupObject.enabled);
};
